import type { ExerciseInfo, ExerciseInfoId, PopulatedExerciseInfo } from '@az3fr/core';
import { Field, Input, Label } from '@headlessui/react';
import { ArrowLeftIcon, MinusIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';

import { Drawer } from '@/components/app/drawer';
import { NativeScrollArea } from '@/components/app/scroll-area';
import { Badge } from '@/components/ui/badge';
import { exerciseSliceSelectors } from '@/entities/exercise/model/exercise.selector';
import { exerciseSearchSliceSelectors } from '@/entities/exercise/model/exercise-search.selector';
import { exerciseSearchSliceActions } from '@/entities/exercise/model/exercise-search.slice';
import { CreateUserExerciseDrawerV2 } from '@/entities/exercise/ui/v2/create-exercise-drawer-v2';
import { useAppDispatch, useAppSelector } from '@/hooks/store.hook';
import { useIsMobile } from '@/hooks/use-is-mobile.hook';

function AddExerciseDrawerV2(props: {
	/**
	 * Whether the drawer is open.
	 */
	isOpen: boolean;
	/**
	 * Callback to close the drawer.
	 */
	close: () => void;
	/**
	 * Callback to give the selected exercises to the parent component.
	 */
	onSelect: (exercises: ExerciseInfo[]) => void;
	/**
	 * Parent can provide a callback in case an exercise is created.
	 */
	onCreateExercise?: (newExerciseId: ExerciseInfoId) => void;
}) {
	const { onSelect, isOpen, close, onCreateExercise } = props;
	const dispatch = useAppDispatch();
	const isMobile = useIsMobile();

	// Selected exercises state
	const [selectedExercises, setSelectedExercises] = useState<PopulatedExerciseInfo[]>([]);

	// Search Input state
	const [liveSearchTerm, setLiveSearchTerm] = useState('');
	const [debouncedSearch] = useDebounce(liveSearchTerm.trim(), 100);

	// Create new exercise state
	const [isCreateUserExerciseDialogOpen, setIsCreateUserExerciseDialogOpen] = useState(false);
	const [newlyCreatedExerciseId, setNewlyCreatedExerciseId] = useState<ExerciseInfoId | null>(null);
	const [temporaryMovementName, setTemporaryMovementName] = useState<string>('');

	// Get the newly created exercise from store when ID is set
	const newlyCreatedExercise = useAppSelector((state) =>
		newlyCreatedExerciseId
			? exerciseSliceSelectors.selectPopulatedExerciseById(state, newlyCreatedExerciseId)
			: undefined,
	);

	// Sync debounced search term to Redux store
	useEffect(() => {
		dispatch(exerciseSearchSliceActions.querySet(debouncedSearch));
	}, [debouncedSearch, dispatch]);

	// Clear search when dialog closes
	useEffect(() => {
		if (!isOpen) {
			dispatch(exerciseSearchSliceActions.queryCleared());
		}
	}, [isOpen, dispatch]);

	// Get current search term from store
	const searchTerm = useAppSelector((state) => state.exerciseSearch.query);

	// Get filtered exercises from store (shows all exercises when no search term)
	const exercises = useAppSelector(exerciseSearchSliceSelectors.selectSearchResults);

	function handleLiveSearchTermChange(e: React.ChangeEvent<HTMLInputElement>) {
		setLiveSearchTerm(e.target.value);
	}

	function handleSearchSubmit(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		// Blur the input to close the mobile keyboard
		// The form submit will handle Enter key automatically
		(e.currentTarget.elements[0] as HTMLInputElement).blur();
	}

	function handleFinishedAddingExercises() {
		if (selectedExercises.length > 0) {
			onSelect(selectedExercises);
			setSelectedExercises([]);
			setLiveSearchTerm('');
			close();
		}
	}

	function handleSelectExercise(exercise: PopulatedExerciseInfo) {
		setSelectedExercises((prev) => [...prev, exercise]);
	}

	function handleRemoveExercise(index: number) {
		setSelectedExercises((prev) => prev.filter((_, i) => i !== index));
	}

	function handleCreateExercise(useSearchTerm = false) {
		setTemporaryMovementName(useSearchTerm ? searchTerm : '');
		setIsCreateUserExerciseDialogOpen(true);
		if (onCreateExercise) {
			onCreateExercise(temporaryMovementName);
		}
	}

	function handleExerciseCreated(exerciseId: ExerciseInfoId) {
		// Set the ID, which will trigger the selector and useEffect
		setNewlyCreatedExerciseId(exerciseId);
	}

	// Effect to auto-add newly created exercise to selected list
	useEffect(() => {
		if (newlyCreatedExercise && newlyCreatedExerciseId) {
			setSelectedExercises((prev) => [...prev, newlyCreatedExercise]);
			// Clear the ID after adding
			setNewlyCreatedExerciseId(null);
		}
	}, [newlyCreatedExercise, newlyCreatedExerciseId]);

	return (
		<>
			{/*--- Create Exercise Dialog - Modal for creating new exercises ---- */}
			<CreateUserExerciseDrawerV2
				isOpen={isCreateUserExerciseDialogOpen}
				close={() => setIsCreateUserExerciseDialogOpen(false)}
				movementQuery={temporaryMovementName}
				onCreate={handleExerciseCreated}
			/>
			{/*--- Main Drawer Container ---- */}
			<Drawer
				fullScreen={isMobile}
				size="md"
				isOpen={isOpen}
				close={close}
				transitionDuration={300}
				direction="left"
				renderCloseButton={(closeDrawer) => (
					<header className="relative border-gray-200 border-b bg-white/80 px-4 py-3 backdrop-blur-sm sm:px-5">
						<div className="flex items-center justify-between gap-3">
							<div className="flex items-center gap-3">
								<button
									type="button"
									onClick={closeDrawer}
									className="flex size-8 shrink-0 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 shadow-sm transition-colors duration-200 hover:border-gray-400 hover:bg-gray-50 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500/20"
									aria-label="Go back"
								>
									<ArrowLeftIcon className="size-4" />
								</button>
								<div className="space-y-1">
									<h3 className="font-semibold text-base text-gray-900 leading-tight sm:text-lg">
										Add Exercises
									</h3>
									<p className="font-medium text-gray-500 text-xs uppercase tracking-widest">
										Movement & load selector
									</p>
								</div>
							</div>
							<div className="flex items-center gap-2">
								<button
									type="button"
									className="inline-flex min-h-8 shrink-0 items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 font-medium text-gray-700 text-xs shadow-sm transition-colors duration-200 hover:border-gray-400 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500/20 sm:text-xs"
									onClick={() => handleCreateExercise(false)}
								>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										viewBox="0 0 16 16"
										fill="currentColor"
										className="size-3.5"
										aria-hidden="true"
									>
										<path d="M8.75 3.75a.75.75 0 0 0-1.5 0v3.5h-3.5a.75.75 0 0 0 0 1.5h3.5v3.5a.75.75 0 0 0 1.5 0v-3.5h3.5a.75.75 0 0 0 0-1.5h-3.5v-3.5Z" />
									</svg>
									Create
								</button>
							</div>
						</div>
					</header>
				)}
			>
				<div className="flex flex-col bg-linear-to-b from-white via-white to-gray-50/60">
					{/*--- Main Content Area - Search and Selected Exercises ---- */}
					<div className="flex flex-1 flex-col gap-5 p-4 sm:px-5">
						{/*--- Search Section - Input and Results ---- */}
						<section>
							<form onSubmit={handleSearchSubmit} className="mb-3">
								<Field>
									<Label className="mb-2 block font-medium text-gray-500 text-xs uppercase tracking-widest">
										Search exercises
									</Label>
									<div className="relative">
										<Input
											placeholder="Search by movement, equipment, or tag"
											value={liveSearchTerm}
											className="h-9 w-full rounded-lg border border-gray-300 bg-white px-3 pr-9 font-medium text-gray-900 text-sm transition-colors duration-200 placeholder:text-gray-500 hover:border-gray-400 focus:border-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500/20 sm:h-10"
											onChange={handleLiveSearchTermChange}
										/>
										{liveSearchTerm && (
											<button
												type="button"
												className="-translate-y-1/2 absolute top-1/2 right-2 rounded-full p-1 text-gray-400 transition-colors duration-200 hover:bg-gray-100 hover:text-gray-600"
												onClick={() => setLiveSearchTerm('')}
												aria-label="Clear search"
											>
												<XMarkIcon className="size-4" />
											</button>
										)}
									</div>
								</Field>
							</form>

							{/*--- Search Results Scrollable Area ---- */}
							{/* @todo: think about more carefully about this height */}
							<NativeScrollArea className="h-[300px] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
								<SearchResults
									searchTerm={liveSearchTerm}
									exercises={exercises}
									onExerciseSelect={handleSelectExercise}
									selectedExercises={selectedExercises}
									onCreateExercise={handleCreateExercise}
								/>
							</NativeScrollArea>
						</section>

						{/*--- Selected Exercises Section - Shows chosen exercises ---- */}
						<SelectedExercises
							selectedExercises={selectedExercises}
							onRemoveExercise={handleRemoveExercise}
						/>
					</div>

					{/*--- Footer Section - Add Exercises Button ---- */}
					<div className="border-gray-200 border-t bg-white/90 px-4 py-4 backdrop-blur-sm sm:px-5">
						<button
							type="button"
							className="w-full rounded-lg bg-gray-900 px-4 py-3 font-semibold text-sm text-white transition-colors duration-200 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40 sm:text-base"
							onClick={handleFinishedAddingExercises}
							disabled={selectedExercises.length === 0}
						>
							{selectedExercises.length === 0
								? 'Select at least one exercise'
								: `Add ${selectedExercises.length} exercise${selectedExercises.length === 1 ? '' : 's'}`}
						</button>
					</div>
				</div>
			</Drawer>
		</>
	);
}

function ExerciseList(props: {
	exercises: PopulatedExerciseInfo[];
	selectedExercises: PopulatedExerciseInfo[];
	onExerciseSelect: (exercise: PopulatedExerciseInfo) => void;
}) {
	const { exercises, onExerciseSelect, selectedExercises } = props;

	return (
		<div className="divide-y divide-gray-100 py-1">
			{exercises.map((exercise) => {
				const selectedCount = selectedExercises.filter(
					(selected) => selected.id === exercise.id,
				).length;

				return (
					<button
						key={exercise.id}
						type="button"
						className="group flex w-full items-center justify-between px-3 py-3 text-start transition-colors duration-200 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
						onClick={() => onExerciseSelect(exercise)}
						aria-label={`Add ${exercise.movement.name}`}
					>
						<div className="flex min-w-0 flex-1 items-center gap-2.5">
							<span className="truncate font-medium text-gray-900 text-sm">
								{exercise.movement.name}
							</span>
							<Badge
								variant="outline"
								className="border-gray-200 bg-gray-50 px-2 py-0.5 text-gray-500 text-xs"
							>
								{exercise.equipment.name}
							</Badge>
						</div>

						{/*--- Selection Counter - Shows how many times this exercise is selected ---- */}
						{selectedCount > 0 && (
							<span className="ms-3 flex size-6 shrink-0 items-center justify-center rounded-full border border-emerald-200 bg-emerald-50 font-medium text-emerald-700 text-xs">
								{selectedCount}
							</span>
						)}
					</button>
				);
			})}
		</div>
	);
}

function SelectedExerciseList(props: {
	exercises: PopulatedExerciseInfo[];
	onRemoveExercise: (index: number) => void;
}) {
	const { exercises, onRemoveExercise } = props;

	return (
		<div className="space-y-2">
			{exercises.map((exercise, index) => {
				const uniqueKey = `${exercise.id}-${index}-${exercise.movement.name}-${exercise.equipment.name}`;
				return (
					<div
						key={uniqueKey}
						className="group flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5 transition-colors duration-100 hover:border-gray-300 hover:bg-gray-50 hover:shadow-sm sm:gap-3.5"
					>
						{/*--- Position Number - Shows order in the list ---- */}
						<div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-gray-100 font-semibold text-gray-700 text-xs sm:size-7">
							{index + 1}
						</div>

						{/*--- Exercise Details - Name and equipment badge ---- */}
						<div className="flex min-w-0 flex-1 flex-col gap-1">
							<span className="wrap-break-word font-medium text-gray-900 text-sm">
								{exercise.movement.name}
							</span>
							<Badge
								variant="outline"
								className="self-start border-gray-200 bg-gray-50 px-2 py-0.5 text-gray-500 text-xs"
							>
								{exercise.equipment.name}
							</Badge>
						</div>

						{/*--- Remove Button - Allows removing this exercise from selection ---- */}
						<button
							type="button"
							className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-gray-400 transition-colors duration-100 hover:bg-red-50 hover:text-red-600 sm:size-9"
							onClick={() => onRemoveExercise(index)}
							aria-label={`Remove ${exercise.movement.name} (position ${index + 1})`}
						>
							<MinusIcon className="size-4" />
						</button>
					</div>
				);
			})}
		</div>
	);
}

function EmptyState(props: {
	message: string;
	showCreateButton?: boolean;
	onCreateNewExerciseClick?: () => void;
	searchedTerm?: string;
}) {
	const { message, showCreateButton, onCreateNewExerciseClick, searchedTerm } = props;

	return (
		<div className="flex h-72 flex-col items-center justify-center px-4">
			<div className="w-full max-w-sm space-y-4 text-center">
				{/*--- Empty State Message ---- */}
				<p className="line-clamp-3 text-center text-gray-500 text-sm">{message}</p>
				{/*--- Create Exercise Button - Shows when search has no results ---- */}
				{showCreateButton && searchedTerm && (
					<div className="w-full">
						<div className="rounded-lg border border-gray-200 bg-white/80 px-4 py-3 shadow-sm">
							<p className="mb-2 text-gray-600 text-xs">Create a new exercise for:</p>
							<button
								type="button"
								className="group w-full cursor-pointer rounded-lg border border-gray-300 bg-gray-50 px-3 py-2.5 text-center font-medium text-gray-900 text-sm transition-colors duration-200 hover:border-gray-400 hover:bg-white"
								onClick={onCreateNewExerciseClick}
							>
								<span className="block truncate">{searchedTerm}</span>
							</button>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}

function SelectedExercises(props: {
	selectedExercises: PopulatedExerciseInfo[];
	onRemoveExercise: (index: number) => void;
}) {
	const { selectedExercises, onRemoveExercise } = props;

	if (selectedExercises.length === 0) {
		return null;
	}

	return (
		<section>
			<div className="mb-2 flex items-center justify-start gap-2">
				<h4 className="font-medium text-gray-600 text-xs uppercase tracking-widest">
					Selected exercises
				</h4>
				<span className="flex size-6 items-center justify-center rounded-full bg-linear-to-br from-emerald-100 to-emerald-200 font-medium text-emerald-700 text-xs">
					{selectedExercises.length}
				</span>
			</div>
			<SelectedExerciseList exercises={selectedExercises} onRemoveExercise={onRemoveExercise} />
		</section>
	);
}

function SearchResults(props: {
	searchTerm: string;
	exercises: PopulatedExerciseInfo[];
	onExerciseSelect: (exercise: PopulatedExerciseInfo) => void;
	selectedExercises: PopulatedExerciseInfo[];
	onCreateExercise: (useSearchTerm: boolean) => void;
}) {
	const { searchTerm, exercises, onExerciseSelect, selectedExercises, onCreateExercise } = props;

	if (searchTerm.length === 1) {
		return (
			<EmptyState message="Type at least 2 characters to search..." showCreateButton={false} />
		);
	}

	if (searchTerm.length > 1 && exercises.length === 0) {
		return (
			<EmptyState
				message={`No exercises found for "${searchTerm}"`}
				searchedTerm={searchTerm}
				showCreateButton={true}
				onCreateNewExerciseClick={() => onCreateExercise(true)}
			/>
		);
	}

	if (exercises.length > 0) {
		return (
			<ExerciseList
				exercises={exercises}
				onExerciseSelect={onExerciseSelect}
				selectedExercises={selectedExercises}
			/>
		);
	}

	return (
		<EmptyState
			message="Start typing to search exercises"
			onCreateNewExerciseClick={() => onCreateExercise(false)}
		/>
	);
}

export { AddExerciseDrawerV2 };
